
import { SWRConfiguration, SWRResponse } from "swr";

import { GetNotesResponse, GetParticipantNotes, Note, UpdatedNoteEvent } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import useGet from "@/hooks/api/useGet";
import { HubConnection } from "@microsoft/signalr/dist/esm";
import { useCallback, useMemo } from "react";
import useSignalROnMessage from "../api/useSignalROnMessage";

const getData = (response?: SWRResponse<GetNotesResponse, GetNotesResponse>) => response?.data?.data;
const getError = (response?: SWRResponse<GetNotesResponse, GetNotesResponse>) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = (response?: SWRResponse<GetNotesResponse, GetNotesResponse>) => ({
    itemMap: response?.data?.data?.reduce<Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, Note[]>>>>>>>((a, b) => {
        if (!b?.studyEventOid || !b?.studyEventRepeat || !b?.caseReportFormOid || !b?.itemName) {
            return a;
        }
        
        if (!a[b.studyEventOid]) {
            a[b.studyEventOid] = {};
        }

        if (!a[b.studyEventOid][b.studyEventRepeat]) {
            a[b.studyEventOid][b.studyEventRepeat] = {};
        }


        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid] = {};
        }
        
        const itemGroup = b.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey;

        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup] = {};
        }

        const itemGroupRepeat = b.itemGroupRepeat ?? 1;

        
        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat] = {};
        }
        
        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName] = [];
        }

        a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName].push(b);

        return a;
    },{})
});

export const useParticipantNotesAndQueries = (participantId?: number, configuration?: SWRConfiguration | undefined) => {
    const request = useMemo(() => {
        if (!participantId) {
            return undefined;
        }

        return () => {
            return new GetParticipantNotes({ participantId });
        }
    }, [participantId]);


    const output = useGet(request, { getData, getError, getAdditionalState }, configuration);

    const onMessage = useCallback(async (connection: HubConnection, data: UpdatedNoteEvent) => {
        if (
            participantId === data.participantId
        )
        {
            await output.refresh();
        }

    }, [
        output.refresh,
        participantId
    ]);

    useSignalROnMessage(AppSettings.channels.note, onMessage);

    return output;
}

export default useParticipantNotesAndQueries;